import React from "react";
import {
  Shield,
  GraduationCap,
  Calendar,
  MapPin,
  DollarSign,
  ChevronRight,
  MessageSquare,
  ChevronLeft,
} from "lucide-react";
import { Senior } from "../../types";

interface ProfilePageProps {
  selectedSenior: Senior | null;
  maskName: (name: string) => string;
  followedPaths: number[];
  setFollowedPaths: (paths: number[]) => void;
  setCurrentPage: (page: string) => void;
  setShowRecommendationResults: (show: boolean) => void;
}

const ProfilePage: React.FC<ProfilePageProps> = ({
  selectedSenior,
  maskName,
  followedPaths,
  setFollowedPaths,
  setCurrentPage,
  setShowRecommendationResults,
}) => {
  if (!selectedSenior) return null;

  const isFollowing = followedPaths.includes(selectedSenior.id);

  return (
    <div className="p-6">
      <div className="flex items-center mb-6">
        <button
          onClick={() => {
            setShowRecommendationResults(true);
            setCurrentPage("recommendation");
          }}
          className="mr-3 p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ChevronLeft className="w-6 h-6 text-gray-600" />
        </button>
        <h2 className="text-2xl font-bold">선배 프로필</h2>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-xl font-semibold flex items-center">
              {maskName(selectedSenior.name)}
              <Shield className="w-4 h-4 ml-2 text-gray-400" />
            </h3>
            <p className="text-gray-600">{selectedSenior.company}</p>
            <p className="text-gray-500">{selectedSenior.position}</p>
          </div>
          <div className="text-center">
            <div className="bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-lg font-semibold">
              {selectedSenior.matchRate}%
            </div>
            <p className="text-xs text-gray-500 mt-1">매칭률</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="flex items-center">
            <GraduationCap className="w-5 h-5 mr-2 text-gray-400" />
            <span>{selectedSenior.university}</span>
          </div>
          <div className="flex items-center">
            <Calendar className="w-5 h-5 mr-2 text-gray-400" />
            <span>{selectedSenior.year}</span>
          </div>
          <div className="flex items-center">
            <MapPin className="w-5 h-5 mr-2 text-gray-400" />
            <span>{selectedSenior.location}</span>
          </div>
          <div className="flex items-center">
            <DollarSign className="w-5 h-5 mr-2 text-gray-400" />
            <span>{selectedSenior.salary}</span>
          </div>
        </div>

        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-600">
            <strong>선배 활동:</strong> 답변 {selectedSenior.responses}개 ·
            도움이 됨 {selectedSenior.helpfulRate}%
          </p>
        </div>
      </div>

      {/* 커리어 타임라인 */}
      <div className="mb-6">
        <h4 className="font-semibold text-lg mb-4">커리어 타임라인</h4>
        <div className="space-y-3">
          {selectedSenior.timeline.map((item, idx) => (
            <div key={idx} className="flex items-start">
              <div className="flex-shrink-0 w-16 text-sm text-gray-500">
                {item.year}
              </div>
              <div className="flex-shrink-0 w-3 h-3 bg-blue-600 rounded-full mt-1 mr-3"></div>
              <div className="flex-1">
                <p className="font-medium">{item.title}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span
                    className={`text-xs px-2 py-1 rounded-full inline-block
                    ${
                      item.type === "education"
                        ? "bg-blue-100 text-blue-700"
                        : ""
                    }
                    ${
                      item.type === "experience"
                        ? "bg-green-100 text-green-700"
                        : ""
                    }
                    ${
                      item.type === "certificate"
                        ? "bg-yellow-100 text-yellow-700"
                        : ""
                    }
                    ${
                      item.type === "achievement"
                        ? "bg-purple-100 text-purple-700"
                        : ""
                    }
                    ${item.type === "career" ? "bg-red-100 text-red-700" : ""}
                    ${
                      item.type === "preparation"
                        ? "bg-gray-100 text-gray-700"
                        : ""
                    }
                  `}
                  >
                    {item.type === "education" && "학력"}
                    {item.type === "experience" && "경험"}
                    {item.type === "certificate" && "자격증"}
                    {item.type === "achievement" && "성과"}
                    {item.type === "career" && "취업"}
                    {item.type === "preparation" && "준비"}
                  </span>
                  {item.weight && (
                    <span className="text-xs text-gray-500">
                      중요도: {"⭐".repeat(item.weight)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <button
          onClick={() => {
            if (!isFollowing) {
              setFollowedPaths([...followedPaths, selectedSenior.id]);
              alert(
                "이 선배의 커리어 경로를 따라가기 시작했습니다! 이제 선배님과 소통할 수 있습니다."
              );
            }
          }}
          className={`w-full py-3 rounded-lg font-semibold transition-all flex items-center justify-center
            ${
              isFollowing
                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                : "bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800"
            }`}
          disabled={isFollowing}
        >
          {isFollowing ? (
            <>✓ 따라가는 중</>
          ) : (
            <>
              경로 따라가기 <ChevronRight className="w-5 h-5 ml-1" />
            </>
          )}
        </button>

        {isFollowing && (
          <button
            onClick={() => setCurrentPage("feedback")}
            className="w-full bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors flex items-center justify-center"
          >
            <MessageSquare className="w-5 h-5 mr-2" />
            선배와 소통하기
          </button>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;